import {
  MEDREC_RULE_NAMES,
  LEGACY_REASSESSMENT_NEEDED,
} from "../constants/medrecRules";

export interface ParsedFlagSummary {
  situation?: string[];
  background?: string[];
  assessment?: string[];
  recommendations?: {
    considerations?: string[];
    most_likely_conditions_not_to_miss?: string[];
  };
  flag_reason?: string;
}

export interface FlagSummaryContent {
  primaryTrigger: string;
  commonlyAssociatedConditions: string[];
}

export interface IFlag {
  flag_id: string;
  flag_summary: string;
  flag_timestamp: string;
  flag_ack_status: number;
}

/**
 * Round any decimal numbers embedded in a free-text string to the nearest
 * integer, leaving everything else untouched. Flag summaries are long narrative
 * strings that can contain values like "RR of 19.5" or "temp 38.2" — we surface
 * the rounded value ("RR of 20", "temp 38") for readability.
 *
 * Only matches whole decimal numbers (digits "." digits), so commas, dates and
 * non-numeric text are left alone.
 *
 * @param text - Arbitrary text that may contain decimal numbers
 * @returns The text with every decimal number rounded to a whole number
 */
export const roundDecimalsInText = (text: string): string => {
  if (!text) return text;
  return text.replace(/\d+\.\d+/g, (match) =>
    String(Math.round(Number(match)))
  );
};

/**
 * Extract all flags from a stringified flags list, sorted by timestamp (most recent first)
 * The flags are pre-filtered to the last N days (I believe 10 days, as of this writing on 11/4/25) by the server. Ask Umer.
 * @param flagsList - The stringified JSON array of flags
 * @returns Array of flag objects sorted by timestamp descending, or empty array if parsing fails
 */
export const getAllFlags = (flagsList: string | null): IFlag[] => {
  if (!flagsList) {
    return [];
  }

  try {
    const flags: IFlag[] = JSON.parse(flagsList);

    if (!Array.isArray(flags)) {
      return [];
    }

    // Sort flags by timestamp in descending order (most recent first)
    return flags.sort((a, b) => {
      const dateA = new Date(a.flag_timestamp).getTime();
      const dateB = new Date(b.flag_timestamp).getTime();
      return dateB - dateA; // Descending order (newest first)
    });
  } catch (error) {
    console.error("Error parsing flags_list:", error);
    return [];
  }
};

/**
 * Extract the most recent flag from a stringified flags list
 * @param flagsList - The stringified JSON array of flags
 * @returns The most recent flag object or null if parsing fails or list is empty
 */
export const getLastFlag = (flagsList: string | null): IFlag | null => {
  const flags = getAllFlags(flagsList);

  if (flags.length === 0) {
    return null;
  }

  // Return the first flag (most recent, since getAllFlags sorts by timestamp descending)
  return flags[0];
};

/**
 * Parse flag summary JSON string (handles double-encoded JSON)
 * @param flagSummary - The flag summary JSON string
 * @returns Parsed flag summary object or null if parsing fails
 */
export const parseFlagSummary = (
  flagSummary: string
): ParsedFlagSummary | null => {
  try {
    let summaryData = JSON.parse(flagSummary);

    // Check if it's still a string (double-encoded JSON)
    if (typeof summaryData === "string") {
      summaryData = JSON.parse(summaryData);
    }

    return summaryData;
  } catch (error) {
    console.error("Error parsing flag_summary:", error);
    return null;
  }
};

/**
 * Extract primary trigger and commonly associated conditions from flag summary
 * @param flagSummary - The flag summary JSON string
 * @returns Object containing primaryTrigger and commonlyAssociatedConditions
 */
export const extractFlagSummaryContent = (
  flagSummary: string
): FlagSummaryContent => {
  const summaryData = parseFlagSummary(flagSummary);
  if (!summaryData) {
    return {
      primaryTrigger: "",
      commonlyAssociatedConditions: [],
    };
  }

  // Extract Primary Trigger from first two assessment points
  let primaryTrigger = "";
  if (summaryData.flag_reason) {
    primaryTrigger = summaryData.flag_reason;
  } else {
    if (summaryData.assessment && Array.isArray(summaryData.assessment)) {
      const firstTwoAssessments = summaryData.assessment.slice(0, 2);
      primaryTrigger = firstTwoAssessments.join(" ");
    }
  }

  // Extract Commonly Associated Conditions from recommendations
  let commonlyAssociatedConditions: string[] = [];
  if (
    summaryData.recommendations &&
    summaryData.recommendations.most_likely_conditions_not_to_miss
  ) {
    commonlyAssociatedConditions =
      summaryData.recommendations.most_likely_conditions_not_to_miss.map(
        (condition: string) => {
          // Extract only the word before the dash "-" or em dash
          let beforeDash = condition.split(" –")[0]; // this is an em dash
          if (beforeDash === condition) {
            beforeDash = condition.split(" -")[0]; // this is a regular dash
          }
          return beforeDash.trim();
        }
      );
  }
  return {
    primaryTrigger,
    commonlyAssociatedConditions,
  };
};

/**
 * Determine tag color based on medical condition type
 * @param condition - The medical condition string
 * @returns Color string ('red' for heart-related, 'blue' for others)
 */
export const getConditionTagColor = (condition: string): string => {
  const conditionLower = condition.toLowerCase();

  // Specific finding types that should be red
  if (
    conditionLower.includes(
      MEDREC_RULE_NAMES.POTENTIAL_TREATMENT_GAP.toLowerCase()
    )
  ) {
    return "red";
  }

  // Specific finding types that should be purple
  if (
    conditionLower.includes(
      MEDREC_RULE_NAMES.PSYCHOTROPIC_MEDS_STEWARDSHIP.toLowerCase()
    )
  ) {
    return "purple";
  }

  // Specific finding types that should be pink
  if (
    conditionLower.includes(MEDREC_RULE_NAMES.MISSING_END_DATE.toLowerCase()) ||
    conditionLower.includes(LEGACY_REASSESSMENT_NEEDED.toLowerCase())
  ) {
    return "pink";
  }

  // Specific finding types that should be orange
  if (
    conditionLower.includes(MEDREC_RULE_NAMES.POTENTIAL_DUPLICATE.toLowerCase())
  ) {
    return "orange";
  }

  // Specific finding types that should be cyan (teal) for safety parameters
  if (
    conditionLower.includes(
      MEDREC_RULE_NAMES.MISSING_SAFETY_PARAMETERS.toLowerCase()
    )
  ) {
    return "cyan";
  }

  // Specific finding types that should be dark brown (using hex color)
  if (conditionLower.includes("med list discrepancy")) {
    return "#654321"; // Dark brown hex color
  }

  // Cardiac conditions (Red)
  const cardiacKeywords = [
    // Existing keywords
    "heart",
    "cardiac",
    "cardio",
    "myocardial",
    "arrhythmia",
    "bradycardia",
    "tachycardia",
    "atrial",
    "ventricular",
    "coronary",
    "angina",
    "infarction",
    "ischemia",
    "congestive",
    "chf",
    "cardiomyopathy",
    "pericardial",
    "valve",
    "mitral",
    "aortic",
    "tricuspid",
    "pulmonary valve",
    "endocarditis",
    "hypertension",
    "heart failure",
    "afib",
    "atrial fibrillation",
    "pacemaker",
    "stent",
    "bypass",
    // New keywords
    "orthostatic hypotension",
    "dvt",
    "deep vein thrombosis",
    "peripheral arterial disease",
    "hemodynamic",
    "hypertensive",
    "anemia",
    "hypotensive",
    "thromboembolic",
    "ischemic",
    "postural hypotension",
    "syncope",
    "hypovolemia",
    "crisis",
    "urgency",
    "extremities",
  ];

  // Respiratory conditions (Blue)
  const respiratoryKeywords = [
    // Existing keywords
    "respiratory",
    "breathing",
    "breath",
    "lung",
    "pulmonary",
    "copd",
    "asthma",
    "bronchitis",
    "pneumonia",
    "emphysema",
    "pleural",
    "bronchial",
    "airway",
    "oxygen",
    "hypoxia",
    "dyspnea",
    "apnea",
    "sleep apnea",
    "tuberculosis",
    "tracheitis",
    "respiratory failure",
    "respiratory distress",
    "respiratory infection",
    "cough",
    "wheeze",
    // New keywords
    "respiratory decompensation",
    "tracheostomy",
    "ventilator",
    "pneumothorax",
    "pneumonitis",
    "hypoxemia",
    "aspiration",
    "trasn",
  ];

  // Infection conditions (Orange)
  const infectionKeywords = [
    "infection",
    "cellulitis",
    "sepsis",
    "uti",
    "urinary tract",
    "bacteremia",
    "wound",
    "covid",
    "infectious",
    "cystitis",
    "parotitis",
    "pressure ulcer",
    "catheter",
    "c. diff",
    "clostridioides",
    "candidal",
  ];

  // Renal conditions (Green)
  const renalKeywords = ["kidney", "renal", "dialysis", "ckd"];

  // Endocrine conditions (Purple)
  const endocrineKeywords = [
    "glycemic",
    "hyperglycemia",
    "hypoglycemia",
    "diabetic",
    "diabetes",
  ];

  // Fluid/Electrolyte/Nutrition conditions (Magenta)
  const fluidElectrolyteKeywords = [
    "fluid",
    "electrolyte",
    "dehydration",
    "nutrition",
    "malnutrition",
    "metabolic",
    "volume",
    "siadh",
    "hyponatremia",
    "hyperkalemia",
    "acidosis",
  ];

  // Falls conditions (Volcano)
  const fallKeywords = ["fall", "falls"];

  // Medication-related conditions (Gold)
  const medicationKeywords = [
    "medication",
    "drug",
    "non-adherence",
    "non-compliance",
    "adverse effect",
    "side effect",
    "serotonin syndrome",
    "opioid",
    "pain control",
    "pain management",
    "ineffective",
    "inadequate",
    "non-adhearence",
  ];

  // Neurological conditions (Pink)
  const neurologicalKeywords = [
    "neurological",
    "neurologic",
    "cerebrovascular",
    "delirium",
    "encephalopathy",
    "stroke",
    "seizure",
    "hydrocephalus",
    "intracranial",
    "subdural",
    "hematoma",
    "agitation",
    "confusion",
    "sedation",
    "ms",
  ];

  // Pain, Musculoskeletal & Functional conditions (very light green)
  const painMusculoskeletalKeywords = [
    "pain",
    "musculoskeletal",
    "orthopedic",
    "post-surgical",
    "osteoarthritis",
    "gout",
    "fracture",
    "injury",
    "knee",
    "shoulder",
    "neck",
    "deconditioning",
    "functional decline",
    "arthritis",
    "postural instability",
    "musculoskeletal",
  ];

  // Gastrointestinal & Genitourinary conditions (cyan)
  const gastrointestinalKeywords = [
    "gastrointestinal",
    "gi",
    "genitourinary",
    "gu",
    "g-tube",
    "hemorrhage",
    "abdominal",
    "constipation",
    "bowel obstruction",
    "diverticulitis",
    "intestinal obstruction",
    "cholangitis",
    "liver",
    "urinary retention",
    "prostatic hyperplasia",
    "bph",
  ];

  if (cardiacKeywords.some((keyword) => conditionLower.includes(keyword))) {
    return "red";
  }
  if (respiratoryKeywords.some((keyword) => conditionLower.includes(keyword))) {
    return "blue";
  }
  if (infectionKeywords.some((keyword) => conditionLower.includes(keyword))) {
    return "orange";
  }
  if (renalKeywords.some((keyword) => conditionLower.includes(keyword))) {
    return "green";
  }
  if (endocrineKeywords.some((keyword) => conditionLower.includes(keyword))) {
    return "purple";
  }
  if (
    fluidElectrolyteKeywords.some((keyword) => conditionLower.includes(keyword))
  ) {
    return "magenta";
  }
  if (fallKeywords.some((keyword) => conditionLower.includes(keyword))) {
    return "volcano";
  }
  if (medicationKeywords.some((keyword) => conditionLower.includes(keyword))) {
    return "gold";
  }
  if (
    neurologicalKeywords.some((keyword) => conditionLower.includes(keyword))
  ) {
    return "pink";
  }
  if (
    painMusculoskeletalKeywords.some((keyword) =>
      conditionLower.includes(keyword)
    )
  ) {
    return "success"; // very light green
  }
  if (
    gastrointestinalKeywords.some((keyword) => conditionLower.includes(keyword))
  ) {
    return "cyan";
  }

  return "default";
};

/**
 * Get all flag summary data with parsed content and colors
 * @param flagSummary - The flag summary JSON string
 * @returns Complete flag summary data with colors and parsed content
 */
export const getFlagSummaryData = (flagSummary: string) => {
  const { primaryTrigger, commonlyAssociatedConditions } =
    extractFlagSummaryContent(flagSummary);

  // Add colors to conditions
  const conditionsWithColors = commonlyAssociatedConditions.map(
    (condition) => ({
      name: condition,
      color: getConditionTagColor(condition),
    })
  );

  return {
    primaryTrigger,
    commonlyAssociatedConditions,
    conditionsWithColors,
    parsedData: parseFlagSummary(flagSummary),
  };
};
